import { getActiveRecommenders } from "@engine/round/accessors";
import {
  MVF,
  MVFProfile,
  TrancheProfile,
  mvfProfileFromTrancheProfile,
  round,
  tranchesFromMVFProfile,
} from "@lib/tranche";
import { type Point, interpolate } from "algorithm/matching";
import { getMarginalValue } from "algorithm/mvfs";
import { RoundAggregate } from "./aggregate";
import { getDefaultTrancheProfile } from "./initEvaluator";
import { normalizeTrancheList } from "./normalizeTrancheList";

export function getMeanRecommendation(
  round: RoundAggregate,
  threshold: number, // How many evaluations there must be for a mean recommender to recommend an evaluable
): TrancheProfile {
  let recommenders = getActiveRecommenders(round);

  // Keep non-mean-recommenders
  recommenders = recommenders.filter(
    (recommender) => recommender?.meanRecommenderThreshold == null,
  );

  if (recommenders.length === 0) {
    return getDefaultTrancheProfile(round.holdProposalId);
  }

  const normalizedMvfProfiles = recommenders
    .map((evaluator) => evaluator.trancheList)
    .map(normalizeTrancheList)
    .map(mvfProfileFromTrancheProfile);

  for (let i = 0; i < recommenders.length; i++) {
    const recommender = recommenders[i];
    const mvfs = normalizedMvfProfiles[i];
    for (const [evalId, evaluation] of Object.entries(
      recommender.evaluations,
    )) {
      if (evaluation.state !== "evaluated") {
        delete mvfs[evalId];
      }
    }
  }

  const meanRecommenderMVFProfile: MVFProfile = calculateMeanRecommendation(
    normalizedMvfProfiles,
    threshold,
  );

  const tranches = tranchesFromMVFProfile(meanRecommenderMVFProfile);

  return normalizeTrancheList(tranches);
}

export function calculateMeanRecommendation(
  normalizedMVFProfiles: MVFProfile[],
  threshold: number = 0,
  lastDollarThreshold: number = 1e9,
  integrationTarget: number = 1e9,
): MVFProfile {
  const evaluationCount = {};
  for (const mvfProfile of normalizedMVFProfiles) {
    for (const evaluableId of Object.keys(mvfProfile)) {
      evaluationCount[evaluableId] = (evaluationCount[evaluableId] || 0) + 1;
    }
  }

  const rescaledMVFProfiles = normalizedMVFProfiles.map((mvfProfile) =>
    rescaleRecommenderMVFs(mvfProfile, lastDollarThreshold, integrationTarget),
  );

  const meanRecommenderMVFProfile: MVFProfile = {};
  for (const evaluableId of Object.keys(evaluationCount)) {
    if (evaluationCount[evaluableId] < threshold) {
      continue;
    }

    const mvfs = rescaledMVFProfiles.map((mvfProfile) =>
      evaluableId in mvfProfile
        ? mvfProfile[evaluableId]
        : [{ dollar: 0, value: 0, placedManually: true }],
    );
    meanRecommenderMVFProfile[evaluableId] = getMeanMVF(mvfs);
  }

  return meanRecommenderMVFProfile;
}

export function getMeanMVF(mvfs: MVF[]): MVF {
  const positionsToSampleSet = new Set<number>();
  for (const mvf of mvfs) {
    for (const point of mvf) {
      positionsToSampleSet.add(point.dollar);
    }
  }
  const positionsToSample = Array.from(positionsToSampleSet).sort(
    (a, b) => a - b,
  );

  const meanMVF: MVF = [];
  for (const dollar of positionsToSample) {
    const value = getAverageValueAtDollar(mvfs, dollar);

    meanMVF.push({
      dollar,
      value,
      placedManually: true,
    });

    if (value === 0) {
      break;
    }
  }

  // if the last position to sample is a vertical jump, then the last point will
  // not be on the x-axis
  const lastPoint = meanMVF[meanMVF.length - 1];
  if (lastPoint.value !== 0) {
    meanMVF.push({
      dollar: lastPoint.dollar,
      value: 0,
      placedManually: true,
    });
  }

  return meanMVF;
}

function getAverageValueAtDollar(mvfs: MVF[], dollar: number): number {
  const totalValue = mvfs.reduce(
    (acc, mvf) => acc + getMarginalValue(dollar, mvf),
    0,
  );

  return round(totalValue / mvfs.length, 3);
}

export function rescaleRecommenderMVFs(
  recommenderMVFs: MVFProfile,
  lastDollarThreshold: number,
  integrationTarget: number,
): MVFProfile {
  const sumOfIntegrals = Object.values(recommenderMVFs).reduce(
    (acc, mvf) => acc + integrateSquaredMVF(mvf, lastDollarThreshold),
    0,
  );

  const k = integrationTarget / sumOfIntegrals;

  return Object.fromEntries(
    Object.entries(recommenderMVFs).map(([evaluableId, mvf]) => [
      evaluableId,
      rescaleMVF(mvf, k),
    ]),
  );
}

function rescaleMVF(mvf: MVF, multiplier: number): MVF {
  return mvf.map((point) => ({
    ...point,
    value: point.value * multiplier,
  }));
}

/**
 * Utility function that integrates y^2 for the line segment from p0 to p1.
 * y = m*x + b, so y^2 = m^2 x^2 + 2 m b x + b^2
 * Antiderivative: (m^2/3) x^3 + m b x^2 + b^2 x
 */
function integrateSquaredMVFSegment([x0, y0]: Point, [x1, y1]: Point): number {
  if (x0 === x1) {
    return 0;
  }

  const m = (y1 - y0) / (x1 - x0);
  const b = y0;

  function antideriv(x: number): number {
    return (m ** 2 / 3) * x ** 3 + m * b * x ** 2 + b ** 2 * x;
  }

  return antideriv(x1) - antideriv(x0);
}

/**
 * Integrate y^2 over a piecewise linear MVF from 0 to lastDollarThreshold.
 * MVF is assumed to be an array of { dollar, value } objects.
 */
function integrateSquaredMVF(mvf: MVF, lastDollarThreshold: number): number {
  let result = 0;
  for (let i = 0; i < mvf.length - 1; i++) {
    let p0: Point = [mvf[i].dollar, mvf[i].value];
    let p1: Point = [mvf[i + 1].dollar, mvf[i + 1].value];

    if (p0[0] >= lastDollarThreshold) {
      break;
    }

    if (p1[0] > lastDollarThreshold) {
      p1 = [lastDollarThreshold, interpolate(p0, p1, lastDollarThreshold)];
    }

    result += integrateSquaredMVFSegment(p0, p1);
  }
  return result;
}
